import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    Mic as MicIcon,
    Stop as StopIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, EMPLOYEE_STORE, STATUS_DRAFT, STATUS_SUBMITTED } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { generateUUID } from '../../../utils/uuid';
import { hasSignatureContent } from '../../../utils/utils';
import Swal from 'sweetalert2';
import { warrantyService } from '../../../services/warrantyService';
import useDictation from '../../../hooks/useDictation';

const WarrantyForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        projectManager: '',
        projectManagerId: '',
        project: '',
        dateOfCompletion: '',
        projectManagerSignature: '',
        status: STATUS_DRAFT
    });

    const [errors, setErrors] = useState({});
    const [jobsLoading, setJobsLoading] = useState(true);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dictationField, setDictationField] = useState(null);
    const sigPadRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
        onError: (error) => {
            console.error('Dictation error:', error);
        }
    });

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const store = tx.objectStore(JOB_STORE);
            const allJobs = await store.getAll();
            const sortedJobs = (allJobs || []).sort((a, b) => {
                const jobA = a.Job || '';
                const jobB = b.Job || '';
                return jobB.localeCompare(jobA);
            });
            setJobs(sortedJobs);
        } catch (error) {
            console.error('Error loading jobs:', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const allEmployees = await store.getAll();
            setEmployees(allEmployees || []);
        } catch (error) {
            console.error('Error loading employees:', error);
        }
    };

    const loadFormData = async () => {
        if (!uuid) return;

        try {
            const data = await warrantyService.getFromIndexedDB(uuid);
            if (data) {
                setForm(data);
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    useEffect(() => {
        loadJobs();
        loadEmployees();
    }, []);

    useEffect(() => {
        if (uuid) {
            loadFormData();
        }
    }, [uuid]);

    const queueSave = (updatedForm) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await warrantyService.saveToIndexedDB(updatedForm);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 500);
    };

    const handleJobChange = (event, newValue) => {
        // Get manager from job if available
        const manager = newValue?.Manager || newValue?.manager || '';

        const updated = {
            ...form,
            jobCode: newValue?.Job || '',
            jobName: newValue?.JobName || '',
            projectManager: manager,
            projectManagerId: '',
            // Clear signature when manager changes
            projectManagerSignature: form.projectManager !== manager ? '' : form.projectManagerSignature
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const toggleDictation = async (fieldName) => {
        if (!dictationSupported) {
            console.error('Speech recognition not supported in this browser');
            return;
        }

        if (isListening && dictationField === fieldName) {
            stopDictation();
            setDictationField(null);
        } else {
            if (isListening) {
                stopDictation();
            }
            setDictationField(fieldName);
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    setForm(currentForm => {
                        const currentText = currentForm[fieldName] || '';
                        const needsSpace = currentText && !currentText.endsWith(' ');
                        const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                        const updated = { ...currentForm, [fieldName]: newText };
                        queueSave(updated);
                        return updated;
                    });
                },
                onError: (e) => {
                    console.error('Recognition error:', e?.error || e);
                }
            });
        }
    };

    const handleSignatureAccept = () => {
        if (hasSignatureContent(sigPadRef)) {
            const signature = sigPadRef.current?.toDataURL() || '';
            const updated = {
                ...form,
                projectManagerSignature: signature
            };
            setForm(updated);
            queueSave(updated);
        }
        setSignatureModalOpen(false);
    };

    const handleEmployeeSelect = (employee) => {
        if (employee) {
            const updated = {
                ...form,
                projectManager: employee.FullName || employee.name || '',
                projectManagerId: employee.id || '',
                // Clear signature when manager changes
                projectManagerSignature: ''
            };
            setForm(updated);
            queueSave(updated);
        }
        setEmployeeModalOpen(false);
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (!form.jobCode) {
            newErrors.jobCode = 'Job is required';
        }

        if (!form.projectManager) {
            newErrors.projectManager = 'Project Manager is required';
        }

        if (!form.dateOfCompletion) {
            newErrors.dateOfCompletion = 'Date of Completion is required';
        }

        if (!form.projectManagerSignature) {
            newErrors.projectManagerSignature = 'Project Manager signature is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Once submitted, the form cannot be edited',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it'
        });

        if (!result.isConfirmed) return;

        setErrors({});
        setSaving(true);

        try {
            const submitData = {
                ...form,
                status: STATUS_SUBMITTED
            };

            // Submit to backend
            await warrantyService.submit(submitData);

            // Delete from IndexedDB after successful submission
            await warrantyService.deleteFromIndexedDB(form.uuid);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Warranty submitted successfully'
            });

            navigate('/operations/warranty');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/operations/warranty')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Warranty</Typography>
                    </Box>
                </Box>

                {/* Job Selection in Header */}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={jobs}
                            getOptionLabel={(option) => `${option.Job || ''} - ${option.JobName || ''}`}
                            value={jobs.find(j => j.Job === form.jobCode) || null}
                            onChange={handleJobChange}
                            loading={jobsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Job"
                                    size="small"
                                    error={!!errors.jobCode}
                                    helperText={errors.jobCode}
                                    required
                                />
                            )}
                            disabled={submitted}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                <Grid container spacing={2}>
                    {/* Main Form Card */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                {/* Project Manager */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Project Manager"
                                        value={form.projectManager || ''}
                                        onClick={() => !submitted && setEmployeeModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={!!errors.projectManager}
                                        helperText={errors.projectManager}
                                        required
                                    />
                                </Grid>

                                {/* Date of Completion */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Date of Completion"
                                        value={form.dateOfCompletion || ''}
                                        onChange={(e) => handleFieldChange('dateOfCompletion', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={!!errors.dateOfCompletion}
                                        helperText={errors.dateOfCompletion}
                                        required
                                        disabled={submitted}
                                        onClick={(e) => {
                                            if (e.target.showPicker) {
                                                e.target.showPicker();
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Project */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Project"
                                        value={form.project || ''}
                                        onChange={(e) => handleFieldChange('project', e.target.value)}
                                        multiline
                                        minRows={2}
                                        maxRows={6}
                                        disabled={submitted}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                padding: '0px',
                                                alignItems: 'flex-start'
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8.5px 14px'
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleDictation('project')}
                                                        color={isListening && dictationField === 'project' ? 'error' : 'primary'}
                                                    >
                                                        {isListening && dictationField === 'project' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    {/* Signature Section */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                Project Manager Signature
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Project Manager"
                                        value={form.projectManager || ''}
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                        {form.projectManagerSignature ? (
                                            <>
                                                <Box
                                                    component="img"
                                                    src={form.projectManagerSignature}
                                                    alt="Signature"
                                                    sx={{
                                                        maxWidth: 200,
                                                        maxHeight: 60,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 1,
                                                        cursor: submitted ? 'default' : 'pointer',
                                                        flexGrow: 1
                                                    }}
                                                    onClick={(e) => {
                                                        if (!submitted) {
                                                            e.preventDefault();
                                                            setSignatureModalOpen(true);
                                                        }
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleFieldChange('projectManagerSignature', '');
                                                        }}
                                                    >
                                                        <ClearIcon />
                                                    </IconButton>
                                                )}
                                            </>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DrawIcon />}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setSignatureModalOpen(true);
                                                }}
                                                fullWidth
                                                disabled={submitted || !form.projectManager}
                                            >
                                                Sign
                                            </Button>
                                        )}
                                    </Box>
                                    {errors.projectManagerSignature && (
                                        <Typography variant="caption" color="error">
                                            {errors.projectManagerSignature}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Fixed Submit Button */}
            {!submitted && (
                <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 2, flexShrink: 0 }}>
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Modals */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.projectManager}
                onAccept={handleSignatureAccept}
                existingSignature={form.projectManagerSignature}
            />

            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleEmployeeSelect}
                title="Select Project Manager"
            />
        </Box>
    );
};

export default WarrantyForm;
